import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Textarea,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiGrid } from "react-icons/fi";
import QrCodeDisplay from "./QrCodeDisplay";

/**
 * Button + modal for students to generate an evaluation QR code.
 * If current time is >30 min before shift end, shows a modal for an explanation note.
 * @param {{ shiftId: string, institutionFormId: string, shiftEndTime: string|Date }} props
 */
export default function StudentEvalQrGenerator({ shiftId, institutionFormId, shiftEndTime }) {
  const { isOpen: isNoteOpen, onOpen: onNoteOpen, onClose: onNoteClose } = useDisclosure();
  const { isOpen: isQrOpen, onOpen: onQrOpen, onClose: onQrClose } = useDisclosure();
  const toast = useToast();

  const [earlyNote, setEarlyNote] = useState("");
  const [qrUrl, setQrUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generateQr = useCallback(async (note) => {
    setGenerating(true);
    try {
      // Get current location
      let latitude = null;
      let longitude = null;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        latitude = pos.coords.latitude;
        longitude = pos.coords.longitude;
      } catch (geoErr) {
        // Location optional
      }

      const response = await fetch("/eval-qr/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          institutionFormId,
          shiftId,
          earlyNote: note || undefined,
          latitude,
          longitude,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to generate QR code");
      }

      setQrUrl(data.qrUrl);
      onNoteClose();
      onQrOpen();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  }, [institutionFormId, shiftId, onNoteClose, onQrOpen, toast]);

  const handleClick = useCallback(() => {
    // Check if >30 min before shift end
    if (shiftEndTime) {
      const endTime = new Date(shiftEndTime);
      const thirtyMinBefore = new Date(endTime.getTime() - 30 * 60 * 1000);
      const now = new Date();

      if (now < thirtyMinBefore) {
        // Early — require note
        onNoteOpen();
        return;
      }
    }

    // Normal timing — generate directly
    generateQr(null);
  }, [shiftEndTime, onNoteOpen, generateQr]);

  return (
    <>
      <Button
        colorScheme="blue"
        variant="outline"
        size="sm"
        leftIcon={<FiGrid />}
        onClick={handleClick}
        isLoading={generating}
        loadingText="Generating..."
      >
        Generate Evaluation QR
      </Button>

      {/* Early generation note modal */}
      <Modal isOpen={isNoteOpen} onClose={onNoteClose}>
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">Early QR Generation</ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Text color="orange.400" fontSize="sm">
                You are generating this QR code more than 30 minutes before the shift ends.
                Please provide a brief explanation.
              </Text>
              <Textarea
                value={earlyNote}
                onChange={(e) => setEarlyNote(e.target.value)}
                placeholder="e.g., Preceptor is leaving early, shift was shortened..."
                bg="gray.700"
                borderColor="gray.600"
                color="white"
                rows={3}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNoteClose} color="gray.400">
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => generateQr(earlyNote)}
              isLoading={generating}
              isDisabled={!earlyNote.trim()}
            >
              Generate QR
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* QR code display modal */}
      <Modal isOpen={isQrOpen} onClose={onQrClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader color="white">Evaluation QR Code</ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text color="gray.400" fontSize="sm" textAlign="center">
                Show this QR code to your preceptor to complete the evaluation form.
              </Text>
              {qrUrl && (
                <QrCodeDisplay url={qrUrl} size={250} label="Scan to complete evaluation" />
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
